import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ProfileService } from '@/features/profile/services/profileService';
import { useUserStore } from '@/common/stores/userStore';
import type {
  StudentProfile,
  StudentReference,
  StudentProfileUpdateData,
  HobbyModel,
  AssistanceModel,
  LanguageModel,
} from '../types';

export const useProfileStore = defineStore('profile', () => {
  // State
  const currentProfile = ref<StudentProfile | null>(null);
  const profiles = ref<Map<number, StudentProfile>>(new Map());
  const hobbies = ref<HobbyModel[]>([]);
  const assistances = ref<AssistanceModel[]>([]);
  const languages = ref<LanguageModel[]>([]);
  const searchResults = ref<StudentReference[]>([]);

  // Loading states
  const isLoading = ref(false);
  const isUpdating = ref(false);
  const isSearching = ref(false);
  const error = ref<string | null>(null);

  // Computed
  const currentProfileId = computed(() => currentProfile.value?.id);
  const currentProfileName = computed(() => {
    if (!currentProfile.value) return '';
    return `${currentProfile.value.firstName} ${currentProfile.value.lastName}`;
  });

  // Actions
  async function fetchCurrentProfile() {
    isLoading.value = true;
    error.value = null;

    try {
      const profile = await ProfileService.getCurrentStudentProfile();
      if (!profile) {
        throw new Error('Current profile not found');
      }
      currentProfile.value = profile;

      // Store in profiles map for caching
      profiles.value.set(profile.id, profile);

      return profile;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch profile';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchProfile(id: number) {
    // Check cache first
    if (profiles.value.has(id)) {
      return profiles.value.get(id)!;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const profile = await ProfileService.getStudentById(id);
      if (!profile) {
        throw new Error('Profile not found');
      }
      profiles.value.set(id, profile);
      return profile;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch profile';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateProfile(data: StudentProfileUpdateData) {
    if (!currentProfile.value) {
      throw new Error('No current profile loaded');
    }

    isUpdating.value = true;
    error.value = null;

    try {
      const updatedProfile = await ProfileService.updateProfile(data);
      currentProfile.value = updatedProfile;
      profiles.value.set(updatedProfile.id, updatedProfile);
      return updatedProfile;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update profile';
      throw err;
    } finally {
      isUpdating.value = false;
    }
  }

  async function updateProfilePhoto(photo: Blob | null) {
    if (!currentProfile.value) {
      throw new Error('No current profile loaded');
    }

    isUpdating.value = true;
    error.value = null;

    try {
      const updatedProfile = await ProfileService.updateStudentPhoto(photo);
      if (!updatedProfile) {
        throw new Error('Failed to update profile photo');
      }
      currentProfile.value = updatedProfile;
      profiles.value.set(updatedProfile.id, updatedProfile);

      // Also update userStore
      const userStore = useUserStore();
      if (updatedProfile.photo) {
        userStore.setUserPhoto(updatedProfile.photo);
      }

      return updatedProfile;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update profile photo';
      throw err;
    } finally {
      isUpdating.value = false;
    }
  }

  async function searchStudents(query: {
    search?: string;
    limit?: number;
    ids?: number[];
    class?: string;
    topic?: string;
    companySiret?: string;
  }) {
    isSearching.value = true;
    error.value = null;

    try {
      const results = await ProfileService.searchStudents(query);
      searchResults.value = results;
      return results;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to search students';
      throw err;
    } finally {
      isSearching.value = false;
    }
  }

  async function reportStudent(studentId: number, reason: string) {
    error.value = null;

    try {
      await ProfileService.reportStudent(studentId, reason);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to report student';
      throw err;
    }
  }

  // Hobbies management
  async function fetchHobbies(filters?: {
    search?: string;
    limit?: number;
    shortlisted?: boolean;
  }) {
    try {
      const result = await ProfileService.getHobbies(filters);
      hobbies.value = result;
      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch hobbies';
      throw err;
    }
  }

  async function addHobby(hobbyId: number) {
    isUpdating.value = true;
    try {
      await ProfileService.addStudentHobby(hobbyId);
      // Update locally - find hobby in available list and add to profile
      if (currentProfile.value) {
        const hobbyToAdd = hobbies.value.find((h) => h.id === hobbyId);
        if (hobbyToAdd) {
          currentProfile.value.hobbies = [...(currentProfile.value.hobbies || []), hobbyToAdd];
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add hobby';
      throw err;
    } finally {
      isUpdating.value = false;
    }
  }

  async function removeHobby(hobbyId: number) {
    isUpdating.value = true;
    try {
      await ProfileService.removeStudentHobby(hobbyId);
      // Update locally - remove hobby from profile
      if (currentProfile.value?.hobbies) {
        currentProfile.value.hobbies = currentProfile.value.hobbies.filter((h) => h.id !== hobbyId);
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to remove hobby';
      throw err;
    } finally {
      isUpdating.value = false;
    }
  }

  async function suggestHobby(label: string) {
    try {
      const newHobby = await ProfileService.suggestHobby(label);
      // Add to hobbies list
      hobbies.value.push(newHobby);
      // Refresh current profile
      if (currentProfile.value) {
        await fetchCurrentProfile();
      }
      return newHobby;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to suggest hobby';
      throw err;
    }
  }

  // Assistances management
  async function fetchAssistances(filters?: {
    search?: string;
    limit?: number;
    shortlisted?: boolean;
  }) {
    try {
      const result = await ProfileService.getAssistances(filters);
      assistances.value = result;
      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch assistances';
      throw err;
    }
  }

  async function addAssistance(assistanceId: number) {
    isUpdating.value = true;
    try {
      await ProfileService.addStudentAssistance(assistanceId);
      // Update locally - find assistance in available list and add to profile
      if (currentProfile.value) {
        const assistanceToAdd = assistances.value.find((a) => a.id === assistanceId);
        if (assistanceToAdd) {
          currentProfile.value.assistances = [
            ...(currentProfile.value.assistances || []),
            assistanceToAdd,
          ];
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add assistance';
      throw err;
    } finally {
      isUpdating.value = false;
    }
  }

  async function removeAssistance(assistanceId: number) {
    isUpdating.value = true;
    try {
      await ProfileService.removeStudentAssistance(assistanceId);
      // Update locally - remove assistance from profile
      if (currentProfile.value?.assistances) {
        currentProfile.value.assistances = currentProfile.value.assistances.filter(
          (a) => a.id !== assistanceId
        );
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to remove assistance';
      throw err;
    } finally {
      isUpdating.value = false;
    }
  }

  // Languages
  async function fetchLanguages() {
    try {
      const result = await ProfileService.getLanguages();
      languages.value = result;
      return result;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch languages';
      throw err;
    }
  }

  // Utility functions
  function clearError() {
    error.value = null;
  }

  function clearSearchResults() {
    searchResults.value = [];
  }

  function clearCache() {
    profiles.value.clear();
    currentProfile.value = null;
  }

  // Initialize data
  function initializeStore() {
    // This could be called on app startup to load initial data
    fetchCurrentProfile().catch(() => {
      // Silently fail if not authenticated or other issues
    });
  }

  return {
    // State
    currentProfile,
    profiles,
    hobbies,
    assistances,
    languages,
    searchResults,
    isLoading,
    isUpdating,
    isSearching,
    error,

    // Computed
    currentProfileId,
    currentProfileName,

    // Actions
    fetchCurrentProfile,
    fetchProfile,
    updateProfile,
    updateProfilePhoto,
    searchStudents,
    reportStudent,

    // Hobbies
    fetchHobbies,
    addHobby,
    removeHobby,
    suggestHobby,

    // Assistances
    fetchAssistances,
    addAssistance,
    removeAssistance,

    // Languages
    fetchLanguages,

    // Utils
    clearError,
    clearSearchResults,
    clearCache,
    initializeStore,
  };
});
